/**
 * A correção privada do rascunho (E160.F3, GUS-145, ADR-0110 §2.2–§2.4). Espelha
 * `apps/api/types/correcao_do_rascunho.py` e `apps/api/types/edicoes_do_laudo.py`.
 *
 * - `GET/POST /casos/:id/correcao/propostas`: o pedido em texto livre vira proposta (rever um campo
 *   ou um documento na fonte, levar à mesa, ou nada), com a prévia na página;
 *   `…/propostas/:pid/aplicar` e `…/descartar` decidem, uma vez só.
 * - `GET /casos/:id/correcao/trechos`, `POST …/edicoes` e `…/edicoes/:eid/reaplicar`: os trechos
 *   editoriais do laudo e a nota do perito por seção, com fundamento.
 *
 * A tela não decide nada: a situação, a prévia, o que sai no laudo e as recusas vêm do servidor.
 */
import axios, { type AxiosInstance } from 'axios';

export type TipoDaProposta = 'campo' | 'documento' | 'mesa' | 'sem_proposta';
export type DecisaoDaProposta = 'aplicada' | 'encaminhada' | 'descartada';
export type SituacaoDaProposta =
  | 'aberta'
  | 'desatualizada'
  | 'aplicada'
  | 'encaminhada'
  | 'descartada';
export type MotivoSemCorrecao = 'grafotecnica' | 'sem_resultado' | 'sem_ficha';
export type MotivoDoPedidoIndisponivel = 'sem_chave' | 'tempo' | 'indisponivel';
export type ResultadoDaReleitura = 'mudou' | 'sem_mudanca' | 'ambigua' | 'nao_localizado';
export type OrigemDoTextoDaFonte = 'camada' | 'imagem';

export type AcaoDaEdicao = 'substituir' | 'remover' | 'restaurar' | 'escrever';
export type TipoDoTrecho = 'texto' | 'campo' | 'nota';
export type MotivoSemEdicao = 'grafotecnica' | 'sem_resultado' | 'nao_aplicavel' | 'indisponivel';
/** E171.F1 (ADR-0122): a saída da edição — o exame ou o relatório de risco. */
export type SaidaDaEdicao = 'exame' | 'riscos';
export type MotivoDaDesatualizacao = 'rodada' | 'trecho_mudou';

export interface FonteDaReleituraData {
  documento_id: string;
  documento_nome: string;
  pagina: number | null;
  original_id: string;
  pagina_no_original: number | null;
  rotulo: string | null;
  trecho: string | null;
  localizado: boolean;
  origem_do_texto: OrigemDoTextoDaFonte | null;
}

export interface CandidataDaReleituraData {
  valor: string;
  literal: string | null;
  fonte: FonteDaReleituraData;
}

export interface PreviaDoCampoData {
  campo: string;
  resultado: ResultadoDaReleitura;
  antes: string | null;
  depois: string | null;
  literal: string | null;
  fonte: FonteDaReleituraData | null;
  candidatas: CandidataDaReleituraData[];
}

export interface ConteudoDaPropostaData {
  pedido: string;
  previa: PreviaDoCampoData[];
}

export interface PropostaDeCorrecaoData {
  id: string;
  case_id: string;
  avaliacao_id: string;
  resultado_sha256: string;
  rodada: number;
  ficha_versao: number;
  tipo: TipoDaProposta;
  campo: string | null;
  documento_id: string | null;
  criada_por: string | null;
  criada_em: string;
  conteudo: ConteudoDaPropostaData;
}

export interface DecisaoGravadaData {
  id: string;
  proposta_id: string;
  decisao: DecisaoDaProposta;
  decidida_por: string | null;
  decidida_em: string;
}

export interface PropostaNaTelaData {
  proposta: PropostaDeCorrecaoData;
  situacao: SituacaoDaProposta;
  decisao: DecisaoGravadaData | null;
}

export interface PropostasDoCasoData {
  case_id: string;
  aplicavel: boolean;
  motivo: MotivoSemCorrecao | null;
  avaliacao_id: string | null;
  rodada: number | null;
  ficha_versao: number | null;
  propostas: PropostaNaTelaData[];
}

export interface AplicacaoData {
  proposta: PropostaNaTelaData;
  releitura: unknown;
  documento: unknown;
}

export interface EdicaoDoLaudoData {
  id: string;
  case_id: string;
  saida: SaidaDaEdicao;
  avaliacao_id: string;
  resultado_sha256: string;
  rodada: number;
  trecho_id: string;
  acao: AcaoDaEdicao;
  texto: string | null;
  fundamento: string;
  base_sha256: string | null;
  anterior_id: string | null;
  criada_por: string | null;
  criada_em: string;
}

export interface TrechoNaTelaData {
  id: string;
  secao: string;
  titulo_da_secao: string;
  rotulo: string;
  tipo: TipoDoTrecho;
  base: string | null;
  base_sha256: string | null;
  vigente: EdicaoDoLaudoData | null;
  no_laudo: string | null;
}

export interface EdicaoDesatualizadaData {
  edicao: EdicaoDoLaudoData;
  motivo: MotivoDaDesatualizacao;
}

export interface TrechosDoLaudoData {
  case_id: string;
  saida: SaidaDaEdicao;
  aplicavel: boolean;
  motivo: MotivoSemEdicao | null;
  avaliacao_id: string | null;
  rodada: number | null;
  assinado: boolean;
  versao_do_catalogo: string;
  limite: string;
  edicoes_sha256: string | null;
  trechos: TrechoNaTelaData[];
  desatualizadas: EdicaoDesatualizadaData[];
  nao_aplicadas: EdicaoDoLaudoData[];
  // E203.F4: a âncora de cada risco do relatório, na ordem do mapa; o documento da mesa liga a
  // linha do mapa ao risco por ela (o número muda quando a ordem muda).
  ancoras_dos_riscos: string[];
}

export interface PedidoDeEdicaoData {
  acao: AcaoDaEdicao;
  texto?: string | null;
  trecho_id: string;
  fundamento: string;
  avaliacao_id: string;
  anterior_id?: string | null;
}

export type MotivoDaFalha =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'sem_resultado'
  | 'sem_ficha'
  | 'sem_protocolo'
  | 'fora_do_eletronico'
  | 'proposta_desatualizada'
  | 'proposta_decidida'
  | 'sem_acao'
  | 'pedido_sem_chave'
  | 'pedido_tempo'
  | 'pedido_indisponivel'
  | 'releitura_esgotada'
  | 'releitura_recusada'
  | 'verificacao_indisponivel'
  | 'peca_certificada'
  | 'outra_rodada'
  | 'outra_edicao'
  | 'assinado'
  | 'trecho_mudou'
  | 'ja_vigente'
  | 'saida_indisponivel'
  | 'texto_longo'
  | 'indisponivel';

export class CorrecaoDoRascunhoError extends Error {
  constructor(readonly motivo: MotivoDaFalha) {
    super(motivo);
    this.name = 'CorrecaoDoRascunhoError';
  }
}

const HTTP_FORBIDDEN = 403;
/** Cada código que as rotas devolvem em texto, os da releitura que `aplicar` chama inclusive. */
export const MOTIVO_POR_CODIGO: Readonly<Record<string, MotivoDaFalha>> = {
  CASE_NOT_FOUND: 'nao_encontrado',
  PROPOSTA_NOT_FOUND: 'nao_encontrado',
  EDICAO_NOT_FOUND: 'nao_encontrado',
  TRECHO_NOT_FOUND: 'nao_encontrado',
  FONTE_NOT_FOUND: 'nao_encontrado',
  PECA_NOT_FOUND: 'nao_encontrado',
  CORRECAO_SEM_RESULTADO: 'sem_resultado',
  EDICAO_SEM_RESULTADO: 'sem_resultado',
  FICHA_NOT_FOUND: 'sem_ficha',
  CASO_SEM_PROTOCOLO: 'sem_protocolo',
  CASE_MODALIDADE_INVALIDA: 'fora_do_eletronico',
  PROPOSTA_DESATUALIZADA: 'proposta_desatualizada',
  PROPOSTA_DECIDIDA: 'proposta_decidida',
  PROPOSTA_SEM_ACAO: 'sem_acao',
  RELEITURA_ESGOTADA: 'releitura_esgotada',
  CAMPO_SEM_RELEITURA: 'releitura_recusada',
  VALOR_SEM_FORMA: 'releitura_recusada',
  DOCUMENTO_SEM_CAMPOS: 'releitura_recusada',
  EXAME_ELETRONICO_CONCLUIDO: 'verificacao_indisponivel',
  EXAME_ELETRONICO_NAO_CONCLUIDO: 'verificacao_indisponivel',
  PECA_CERTIFICADA: 'peca_certificada',
  LAUDO_JA_ASSINADO: 'assinado',
  TRECHO_MUDOU: 'trecho_mudou',
  EDICAO_JA_VIGENTE: 'ja_vigente',
  SAIDA_INDISPONIVEL: 'saida_indisponivel',
  TEXTO_LONGO: 'texto_longo',
  // A tela só oferece as ações do tipo; a recusa do servidor não tem o que explicar a mais.
  ACAO_INVALIDA: 'indisponivel',
};
const MOTIVO_DO_PEDIDO: Record<MotivoDoPedidoIndisponivel, MotivoDaFalha> = {
  sem_chave: 'pedido_sem_chave',
  tempo: 'pedido_tempo',
  indisponivel: 'pedido_indisponivel',
};
const MOTIVO_DA_EDICAO: Record<string, MotivoDaFalha> = {
  rodada: 'outra_rodada',
  outra_edicao: 'outra_edicao',
};

interface Detalhe {
  code?: string;
  motivo?: string;
}

function motivoDoDetalhe(detalhe: unknown): MotivoDaFalha {
  if (typeof detalhe === 'string') return MOTIVO_POR_CODIGO[detalhe] ?? 'indisponivel';
  const { code, motivo } = (detalhe ?? {}) as Detalhe;
  if (code === 'PEDIDO_INDISPONIVEL') {
    return MOTIVO_DO_PEDIDO[motivo as MotivoDoPedidoIndisponivel] ?? 'pedido_indisponivel';
  }
  if (code === 'EDICAO_DESATUALIZADA') return MOTIVO_DA_EDICAO[motivo ?? ''] ?? 'outra_edicao';
  return 'indisponivel';
}

/** O motivo da recusa pelo código do servidor (texto, ou `{code, motivo}`). */
export function motivoDoErro(err: unknown): MotivoDaFalha {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  if (err.response?.status === HTTP_FORBIDDEN) return 'sem_permissao';
  return motivoDoDetalhe((err.response?.data as { detail?: unknown } | undefined)?.detail);
}

/** O pedido espera a escolha e a prévia na página: a espera é a do serviço externo. */
export const PEDIR_CORRECAO_TIMEOUT_MS = 90_000;

async function chamar<T>(pedido: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await pedido()).data;
  } catch (err) {
    throw new CorrecaoDoRascunhoError(motivoDoErro(err));
  }
}

const base = (caseId: string) => `/casos/${caseId}/correcao`;

export function lerPropostas(client: AxiosInstance, caseId: string): Promise<PropostasDoCasoData> {
  return chamar(() => client.get<PropostasDoCasoData>(`${base(caseId)}/propostas`));
}

export function pedirCorrecao(
  client: AxiosInstance,
  caseId: string,
  pedido: string,
): Promise<PropostaNaTelaData> {
  return chamar(() =>
    client.post<PropostaNaTelaData>(
      `${base(caseId)}/propostas`,
      { pedido },
      { timeout: PEDIR_CORRECAO_TIMEOUT_MS },
    ),
  );
}

export function aplicarProposta(
  client: AxiosInstance,
  caseId: string,
  propostaId: string,
): Promise<AplicacaoData> {
  return chamar(() =>
    client.post<AplicacaoData>(`${base(caseId)}/propostas/${propostaId}/aplicar`, undefined, {
      timeout: PEDIR_CORRECAO_TIMEOUT_MS,
    }),
  );
}

export function descartarProposta(
  client: AxiosInstance,
  caseId: string,
  propostaId: string,
): Promise<PropostaNaTelaData> {
  return chamar(() =>
    client.post<PropostaNaTelaData>(`${base(caseId)}/propostas/${propostaId}/descartar`),
  );
}

export function lerTrechos(client: AxiosInstance, caseId: string): Promise<TrechosDoLaudoData> {
  return chamar(() => client.get<TrechosDoLaudoData>(`${base(caseId)}/trechos`));
}

export function editarTrecho(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeEdicaoData,
): Promise<TrechoNaTelaData> {
  return chamar(() => client.post<TrechoNaTelaData>(`${base(caseId)}/edicoes`, pedido));
}

export function reaplicarEdicao(
  client: AxiosInstance,
  caseId: string,
  edicaoId: string,
): Promise<TrechoNaTelaData> {
  return chamar(() =>
    client.post<TrechoNaTelaData>(`${base(caseId)}/edicoes/${edicaoId}/reaplicar`),
  );
}

/** As ações de cada tipo de trecho, na ordem dos botões. E184.F2: o campo `risco` também se tira
 * (o risco inteiro sai do relatório); os outros campos, não. */
export const ACOES_DO_TIPO: Record<TipoDoTrecho, AcaoDaEdicao[]> = {
  texto: ['substituir', 'remover', 'restaurar'],
  campo: ['substituir', 'remover', 'restaurar'],
  nota: ['escrever', 'remover'],
};

export const ACOES_COM_TEXTO: ReadonlySet<AcaoDaEdicao> = new Set<AcaoDaEdicao>([
  'substituir',
  'escrever',
]);

/** Mínimo do fundamento, o mesmo do servidor. */
export const FUNDAMENTO_MINIMO = 15;
export const PEDIDO_MINIMO = 10;
